// Custom hook for managing pairing token

import { useState, useEffect, useCallback } from 'react';
import { storageService } from '../services/storageService';

export const usePairingToken = () => {
  const [token, setToken] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState(0);

  const checkToken = useCallback(() => {
    setIsChecking(true);

    // Small delay for UX
    setTimeout(() => {
      const storedToken = storageService.getPairToken();
      const tokenValid = storageService.isTokenValid();
      const daysLeft = storageService.getDaysUntilExpiry();

      setToken(storedToken);
      setIsValid(tokenValid);
      setDaysUntilExpiry(daysLeft);
      setIsChecking(false);
    }, 300);
  }, []);

  useEffect(() => {
    checkToken();

    // Listen for storage changes (when token is saved in another component)
    const handleStorageChange = (e) => {
      if (e.key === 'vr_pair_token' || e.key === null) {
        // Token was added/removed, recheck
        checkToken();
      }
    };

    // Listen for custom event (for same-window updates)
    const handleTokenUpdate = () => {
      checkToken();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tokenUpdated', handleTokenUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenUpdated', handleTokenUpdate);
    };
  }, [checkToken]);

  const saveToken = useCallback((newToken, userId) => {
    const saved = storageService.savePairToken(newToken, userId);
    if (saved) {
      setToken(newToken);
      setIsValid(true);
      setDaysUntilExpiry(90);

      // Notify other instances of this hook
      window.dispatchEvent(new Event('tokenUpdated'));

      return true;
    }
    return false;
  }, []);

  const clearToken = useCallback(() => {
    const cleared = storageService.clearPairingData();
    if (cleared) {
      setToken(null);
      setIsValid(false);
      setDaysUntilExpiry(0);

      // Notify other instances of this hook
      window.dispatchEvent(new Event('tokenUpdated'));

      return true;
    }
    return false;
  }, []);

  return {
    token,
    isValid,
    isChecking,
    daysUntilExpiry,
    saveToken,
    clearToken,
    recheckToken: checkToken
  };
};
